import { Integration } from "@/types/general";
import TwentyFourSevenOfficeImportModal from "@components/Dashboard/pages/SettingsPage/Integrations/ThirdPartyIntegrations/components/modals/import/TwentyFourSevenOfficeImportModal";
import TripletexSettingsModal from "@components/Dashboard/pages/SettingsPage/Integrations/ThirdPartyIntegrations/components/modals/settings/TripletexSettingsModal";
import WooCommerceIntegrationModal from "@components/Dashboard/pages/SettingsPage/Integrations/ThirdPartyIntegrations/integrations/WooCommerceIntegrationModal";
import BaseButton from "@reusables/BaseButton";
import BaseChip from "@reusables/BaseChip";
import BaseTooltip from "@reusables/BaseTooltip";
import { memo, useState } from "react";
import { useTranslation } from "react-i18next";
import TripletexImportModal from "src/components/Dashboard/pages/SettingsPage/Integrations/ThirdPartyIntegrations/components/modals/import/TripletexImportModal";
import PowerofficeImportModal from "../modals/import/PowerofficeImportModal";
import ShopifyImportModal from "../modals/import/ShopifyImportModal";
import SusoftImportModal from "../modals/import/SusoftImportModal";
import SusoftExportModal from "../modals/export/SusoftExportModal";
import PowerOfficeSettingsModal from "../modals/settings/PowerOfficeSettingsModal";

interface IntegrationCardProps {
    name: string;
    description: string;
    imageUrl: string;
    integrated: boolean;
    onModalOpen: () => void;
    hidden?: boolean;
    slug: Integration.Components.Slug;
    isIntegrationsLimitReached: boolean;
}

const isImportAvailable = (slug: Integration.Components.Slug) =>
    slug === "tripletex" ||
    slug === "poweroffice" ||
    slug === "twenty_four_seven_office" ||
    slug === "shopify" ||
    slug === "susoft";

const isExportAvailable = (slug: Integration.Components.Slug) => slug === "susoft";

const isConfigurationAvailable = (slug: Integration.Components.Slug) =>
    slug === "poweroffice" || slug === "tripletex" || slug === "woocommerce";

export default memo(function IntegrationCard({
    name,
    description,
    imageUrl,
    onModalOpen,
    hidden,
    integrated,
    slug,
    isIntegrationsLimitReached,
}: IntegrationCardProps) {
    const { t } = useTranslation("", { keyPrefix: "settings.integrations.thirdParty.buttons" });

    const [isImportModalOpen, setIsImportModalOpen] = useState(false);
    const [isExportModalOpen, setIsExportModalOpen] = useState(false);
    const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);

    return (
        <>
            <li className="flex-row space-x-[20px]" style={{ display: hidden ? "none" : "flex" }}>
                <div className={"grow flex flex-row space-x-4"}>
                    <div>
                        <img src={imageUrl} alt={name} className="w-[80px]" />
                    </div>
                    <div>
                        <div className={"flex flex-row space-x-4"}>
                            <p className="text-2xl text-[#3c3769]">{name}</p>
                            {integrated && (
                                <div className={"mt-[4px]"}>
                                    <BaseChip fill={"#A6DD4C"} asDot>
                                        Integrated
                                    </BaseChip>
                                </div>
                            )}
                        </div>
                        <p>{description}</p>
                    </div>
                </div>
                <div className={"flex justify-center items-center gap-2"}>
                    {isImportAvailable(slug) && integrated && (
                        <BaseButton
                            onClick={() => void setIsImportModalOpen(true)}
                            text={t("import")}
                            size={"small"}
                            buttonWidth={"150px"}
                        />
                    )}
                    {isExportAvailable(slug) && integrated && (
                        <BaseButton
                            onClick={() => void setIsExportModalOpen(true)}
                            text={t("export")}
                            size={"small"}
                            buttonWidth={"150px"}
                        />
                    )}
                    {isConfigurationAvailable(slug) && integrated && (
                        <BaseButton
                            onClick={() => void setIsSettingsModalOpen(true)}
                            text={t("settings")}
                            size={"small"}
                            buttonWidth={"150px"}
                        />
                    )}
                    {!integrated && (
                        <BaseTooltip
                            title={t("integrationsLimitReached") as string}
                            active={isIntegrationsLimitReached}
                        >
                            <BaseButton
                                onClick={onModalOpen}
                                text={t("integrate")}
                                size={"small"}
                                primaryOutlined
                                buttonWidth={"150px"}
                                disabled={isIntegrationsLimitReached}
                            />
                        </BaseTooltip>
                    )}

                    {integrated && (
                        <BaseButton
                            onClick={onModalOpen}
                            text={t("disable")}
                            size="small"
                            primaryOutlined
                            buttonWidth="150px"
                            borderColor={"#E25C5C"}
                            textColor={"#E25C5C"}
                        />
                    )}
                </div>
            </li>

            {slug === "tripletex" && integrated && (
                <TripletexImportModal
                    isOpen={isImportModalOpen}
                    onClose={() => setIsImportModalOpen(false)}
                />
            )}

            {slug === "poweroffice" && integrated && (
                <PowerofficeImportModal
                    isOpen={isImportModalOpen}
                    onClose={() => setIsImportModalOpen(false)}
                />
            )}

            {slug === "shopify" && integrated && (
                <ShopifyImportModal
                    isOpen={isImportModalOpen}
                    onClose={() => setIsImportModalOpen(false)}
                />
            )}

            {slug === "poweroffice" && integrated && (
                <PowerOfficeSettingsModal
                    isOpen={isSettingsModalOpen}
                    onClose={() => setIsSettingsModalOpen(false)}
                />
            )}

            {slug === "tripletex" && integrated && (
                <TripletexSettingsModal
                    isOpen={isSettingsModalOpen}
                    onClose={() => setIsSettingsModalOpen(false)}
                />
            )}

            {slug === "twenty_four_seven_office" && integrated && (
                <TwentyFourSevenOfficeImportModal
                    isOpen={isImportModalOpen}
                    onClose={() => setIsImportModalOpen(false)}
                />
            )}

            {slug === "woocommerce" && integrated && (
                <WooCommerceIntegrationModal
                    isOpen={isSettingsModalOpen}
                    onClose={() => setIsSettingsModalOpen(false)}
                />
            )}

            {slug === "susoft" && integrated && (
                <SusoftImportModal
                    isOpen={isImportModalOpen}
                    onClose={() => setIsImportModalOpen(false)}
                />
            )}

            {slug === "susoft" && integrated && (
                <SusoftExportModal
                    isOpen={isExportModalOpen}
                    onClose={() => setIsExportModalOpen(false)}
                />
            )}
        </>
    );
});
